import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import {
    useForm,
    useAuth,
    useFetch,
    useResource,
    useGrid,
    useModal,
    useStore,
    useToast,
    usePopup,
    TFormSchema,
    usePage,
} from "@/comn/hooks"; // hook
import {
    BASE,
    URLS,
    APIS,
    SF_BTCH_EXCUTN_RSLT_FORM,
    SG_BTCH_EXCUTN_RSLT_STEP_LIST,
} from "@/tra/tancis/ptli/btch/excutnrslt/service/PtliBtchExcutnRsltService";

/*
 * Managing batch runs Result Details
 * !== 배치실행결과관리 상세 ==!
 */
export const PTLI1204002Q = (props: any) => {
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    const { pgeUid, menuId } = usePage({
        auth: ["auth_ptli0015", "auth_ptlianyADM", "auth_ptli0047", "auth_ptli0048", "auth_ptli0049"],
    });
    const { t } = useTranslation(); // Translation Hook !== 언어 변화 Hook ==!
    const navigate = useNavigate(); // Navigate Hook !== 화면 이동 Hook ==!
    const { jobExecutionId } = useParams();
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { pgeStore, setStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast(); // Toast Message Hook !== Toast 메세지 표시 Hook ==!
    const auth = useAuth(); // Login , User Information Hook !== 로그인, 유저정보 Hook ==!
    const searchParams = new URLSearchParams(window.location.search);
    const bsopClsfCd = searchParams.get("bsopClsfCd"); // Work classification code received from the list screen  !== 목록 화면에서 전달 받은 업무 분류 코드  ==!

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // Managing batch runs Result Form !== 배치실행결과관리 Form ==!
        btchExcutnRsltForm: useForm({
            defaultSchema: SF_BTCH_EXCUTN_RSLT_FORM,
            defaultValues: {
                ...(pgeStore?.form || {}),
            },
        }),
    };

    /*
     * Declaration Grid
     * !== 그리드 정의 ==!
     */
    const grid = {
        btchExcutnStepList: useGrid({
            defaultSchema: SG_BTCH_EXCUTN_RSLT_STEP_LIST,
            page: pgeStore?.page,
            size: pgeStore?.size,
        }),
    };

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        getBtchExcutnRsltInfo: useFetch({
            api: () => APIS.getBtchExcutnRsltInfo(jobExecutionId, bsopClsfCd),
            onSuccess: (data: any) => {
                if (!data?.excutnRsltInfo?.content) {
                    modal.openModal({
                        content: "msg.com.00034", // No data found. !== 해당 데이터가 없습니다. ==!
                        onConfirm: () => {
                            navigate(URLS.ptli1204001q);
                        },
                        close: false,
                    });

                    return;
                }

                form.btchExcutnRsltForm.setValues(data.excutnRsltInfo.content);
                grid.btchExcutnStepList.setData({ content: data.excutnRsltInfo.content.ptliExcutnStepList });
            },
            onError: (error) => {
                if (error?.response?.status === 400) {
                    const { errorMessage } = error.response.data;

                    modal.openModal({
                        content: errorMessage.content,
                        onConfirm: () => {
                            navigate(URLS.ptli1204001q);
                        },
                        close: false,
                    });

                    return;
                }

                toast.showToast({ type: "error", content: "msg.com.00034" }), // No data found. !== 해당 데이터가 없습니다. ==!
                    navigate(URLS.ptli1204001q);
            },
        }),

        updateBtchExcutnRslt: useFetch({
            api: (info: any) => {
                info.bsopClsfCd = bsopClsfCd;
                return APIS.updateBtchExcutnRslt(info);
            },
            enabled: false,
            onSuccess: () => {
                modal.openModal({ content: "msg.com.00059" }); // Successfully updated. !== 정상적으로 수정되었습니다.==!
            },
            onError: () => toast.showToast({ type: "error", content: "msg.com.00022" }), // Failed to update. !== 수정이 실패하였습니다.==!
        }),
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        onListBtnClick: () => navigate(URLS.ptli1204001q),

        onEditBtnClick: form.btchExcutnRsltForm.handleSubmit(
            () => {
                const data = form.btchExcutnRsltForm.getValues();
                modal.openModal({
                    content: "msg.com.00050", // Confirm update? !== 수정하시겠습니까? ==!
                    onConfirm: () => fetch.updateBtchExcutnRslt.fetch(data),
                });
            },
            () => toast.showToast({ type: "warning", content: "msg.00002" }), // Data is invalid, !== 데이터가 유효하지않습니다. ==!
        ),
    };

    /*
     * Declaration Event Render
     * !== 랜더 정의 ==!
     */
    const render = {
        grid_btchExcutnStepList: {
            cell: {
                srch: (arg: any) => {
                    const { binding, rowValues, value } = arg;
                    const data = { exitMessage: rowValues.exitMessage };

                    if (rowValues.exitCode == "FAILED") {
                        return (
                            <Button
                                variant="secondary"
                                onClick={() => {
                                    const handlePopup = (data: any) => {
                                        // 모든 Modal 창 닫기
                                        modal.closeModal();
                                    };
                                    modal.openModal({
                                        content: <PTLI1204004Q data={data} handlePopup={handlePopup} />,
                                        size: "sm",
                                        draggable: true,
                                    });
                                }}
                            >
                                {t("T_SRCH")}
                            </Button>
                        );
                    }
                },
            },
        },
    };

    useEffect(() => {
        fetch.getBtchExcutnRsltInfo.fetch();
    }, []);

    return (
        <Page>
            <Group>
                <Group.Body>
                    <Group.Section>
                        <Group.Row>
                            <Group.Control {...form.btchExcutnRsltForm.schema.jobExecutionId} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.btchExcutnRsltForm.schema.jobInstanceId} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.btchExcutnRsltForm.schema.version} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.btchExcutnRsltForm.schema.startTime} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.btchExcutnRsltForm.schema.endTime} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.btchExcutnRsltForm.schema.status} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.btchExcutnRsltForm.schema.exitCode} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.btchExcutnRsltForm.schema.exitMessage} controlSize={10} />
                        </Group.Row>
                        <Group.Row>
                            <Group.Control {...form.btchExcutnRsltForm.schema.lastUpdated} controlSize={10} />
                        </Group.Row>
                    </Group.Section>
                </Group.Body>
            </Group>

            <Group>
                <Group.Body>
                    <Group.Section>
                        <Grid {...grid.btchExcutnStepList.grid} render={render.grid_btchExcutnStepList} />
                    </Group.Section>
                </Group.Body>
            </Group>

            <Layout direction="row">
                <Layout.Left>
                    <Button
                        role="list"
                        onClick={() => {
                            navigate(URLS.ptli1204001q);
                        }}
                    />
                </Layout.Left>
                <Layout.Right>
                    <Layout.Button
                        role={"save"}
                        onClick={() => {
                            handler.onEditBtnClick();
                        }}
                    />
                </Layout.Right>
            </Layout>
        </Page>
    );
};

// Modal로 띄울 컴포넌트
export const PTLI1204004Q = (props: any) => {
    const { data } = props;
    const { pgeUid } = usePage({ pgeUid: "ptli1204004q" });

    // 현재 화면설계 참고 -> 항목과 검색어 입력폼 명은 개발에 맞게 변경 해주세요(현재는 라벨이없어서 임시로 넣어놓았습니다)
    const SG_FORM: TFormSchema = {
        id: "form",
        schema: {
            exitMessage: {
                type: "textarea",
                edit: false,
            },
        },
    };

    const form = useForm({
        defaultSchema: SG_FORM,
        defaultValues: { ...data },
    });

    return (
        <>
            <Page pgeUid={pgeUid} info={true}>
                <Group>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.schema.exitMessage} />
                            </Group.Row>
                        </Group.Section>
                    </Group.Body>
                </Group>
            </Page>
        </>
    );
};
